// Believable research data used across screens. Papers, benchmarks, projects.

const PAPERS = [
  {
    id: 'p1',
    title: 'GS-DiT: Gaussian Splatting with Diffusion Transformers for Real-Time 4D Scene Synthesis',
    authors: ['M. Tancik', 'A. Yu', 'K. Park', 'B. Mildenhall', 'P. Srinivasan'],
    venue: 'CVPR 2026',
    date: '2026.05.16',
    arxiv: '2605.04812',
    tags: ['4DGS', 'diffusion', 'real-time'],
    dataset: 'D-NeRF',
    code: 'released',
    summary: 'Couples a deformable Gaussian splatting field with a lightweight diffusion transformer prior, achieving 31.7 PSNR on D-NeRF dynamic scenes at 84 FPS — a 3.1× speedup over Deformable-3DGS while removing flicker on thin geometry.',
    why: 'Closest match to your "server-rack digital twin" project — handles thin metallic geometry your D-NeRF baseline struggles with.',
    worth: 0.92, novel: 0.81, impl: 0.74,
    must: true,
  },
  {
    id: 'p2',
    title: 'OpenVLA-2: Open-Source Generalist Vision-Language-Action with 7B-Parameter Mixture-of-Embodiments',
    authors: ['M. Kim', 'K. Pertsch', 'S. Karamcheti', 'T. Xiao', 'D. Sadigh'],
    venue: 'CoRL 2025',
    date: '2026.05.16',
    arxiv: '2605.04199',
    tags: ['VLA', 'foundation', 'manipulation'],
    dataset: 'Open-X',
    code: 'released',
    summary: 'Scales open-source VLA training to 1.2M robot trajectories across 38 embodiments with a sparse mixture-of-experts head. Reports 71.4% success on ALOHA bimanual tasks zero-shot, +14.3 over OpenVLA.',
    why: 'You bookmarked OpenVLA (Mar 2025); this is the direct successor with real-robot evals on ALOHA.',
    worth: 0.89, novel: 0.74, impl: 0.83,
  },
  {
    id: 'p3',
    title: 'Sparse-View 3DGS via Geometric Self-Distillation from a Single Wide-Angle Image',
    authors: ['Y. Liu', 'H. Ren', 'J. Wang', 'Z. Lin'],
    venue: 'ICCV 2025',
    date: '2026.05.15',
    arxiv: '2605.03761',
    tags: ['3DGS', 'sparse-view', 'monocular'],
    dataset: 'DL3DV-10K',
    code: 'released',
    summary: 'Reconstructs a Gaussian splat field from as few as three input views by distilling depth and normal priors from a pre-trained monocular foundation model. 26.8 PSNR on DL3DV sparse-view split.',
    why: 'Sparse-view setup matches your data-collection constraints in the rack twin project.',
    worth: 0.84, novel: 0.71, impl: 0.69,
  },
  {
    id: 'p4',
    title: 'Deformation-Field Caching for 4D Gaussian Splatting at 120 Hz',
    authors: ['J. Park', 'S. Kim', 'M. Choi'],
    venue: 'SIGGRAPH 2026',
    date: '2026.05.15',
    arxiv: '2605.03104',
    tags: ['4DGS', 'deformation', 'fast'],
    dataset: 'HyperNeRF',
    code: 'pending',
    summary: 'Precomputes a quantized deformation cache indexed by canonical Gaussian id, eliminating per-frame MLP evaluation. 31.2 PSNR on HyperNeRF at 120 Hz on a single 4090.',
    why: 'Matches the deformation-field thread in your wiki and your 4D-recon project reading queue.',
    worth: 0.81, novel: 0.86, impl: 0.71,
  },
  {
    id: 'p5',
    title: 'RoboFM: A 13B Foundation Model for Tabletop and Mobile Manipulation Trained on 4.2M Episodes',
    authors: ['A. Brohan', 'C. Finn', 'S. Levine', 'K. Hausman'],
    venue: 'NeurIPS 2025',
    date: '2026.05.14',
    arxiv: '2605.02580',
    tags: ['foundation', 'VLA', 'mobile'],
    dataset: 'BridgeData-V3',
    code: 'released',
    summary: 'Mixes language-conditioned manipulation across 11 embodiments with a unified action tokenizer. Strongest reported zero-shot on Bridge V3 (62.1%) and DROID held-out tasks (54.8%).',
    why: 'Your VLA-embodiment project tracks generalist foundation models with real-robot evaluation.',
    worth: 0.88, novel: 0.72, impl: 0.66,
  },
  {
    id: 'p6',
    title: 'NeRF-Triplane Compression with Adaptive Bitrate Allocation for Edge Deployment',
    authors: ['R. Cao', 'L. Mescheder'],
    venue: 'ECCV 2025',
    date: '2026.05.14',
    arxiv: '2605.02012',
    tags: ['NeRF', 'compression', 'edge'],
    dataset: 'MipNeRF-360',
    code: 'released',
    summary: '7.4× compression of triplane NeRFs by jointly optimizing rate and reconstruction with an entropy-coded VQ codebook; 25.9 PSNR on MipNeRF-360 at 28 MB per scene.',
    worth: 0.71, novel: 0.62, impl: 0.78,
  },
  {
    id: 'p7',
    title: 'Stop Citing PSNR: A Perceptual Benchmark for Dynamic Scene Reconstruction',
    authors: ['E. Foster', 'P. Henderson', 'A. Davison'],
    venue: 'arXiv 2026',
    date: '2026.05.13',
    arxiv: '2605.01577',
    tags: ['benchmarking', '4DGS', 'metrics'],
    dataset: 'D-NeRF',
    code: 'released',
    summary: 'Argues that PSNR/SSIM masks temporal artifacts in 4D reconstruction; proposes T-VMAF and a leaderboard re-ranking the top 14 4DGS methods.',
    why: 'Directly affects how you interpret results in your benchmark memory.',
    worth: 0.76, novel: 0.69, impl: 0.31,
  },
  {
    id: 'p8',
    title: 'SfM in 90 Seconds: Globally Consistent Pose Recovery via Learned Pairwise Priors',
    authors: ['N. Karaev', 'I. Rocco', 'J. Schönberger'],
    venue: 'CVPR 2026',
    date: '2026.05.13',
    arxiv: '2605.01202',
    tags: ['SfM', 'pose', 'fast'],
    dataset: 'IMC-2024',
    code: 'released',
    summary: 'Reaches 0.82 mAA@10° on the IMC-2024 hidden set in 90 s per scene, replacing global BA with a learned pose-refinement transformer.',
    worth: 0.79, novel: 0.74, impl: 0.81,
  },
];

const BENCHMARKS = [
  // dataset, task, condition, method, paper, metric, value, code, source, conf, status
  { d: 'D-NeRF',      t: 'Dynamic NVS', c: '8 views, 200 frames',   m: 'GS-DiT',           p: 'p1', metric: 'PSNR ↑',  v: 31.74, code: true,  src: 'Tbl. 2, p. 6', conf: 0.96, s: 'verified' },
  { d: 'D-NeRF',      t: 'Dynamic NVS', c: '8 views, 200 frames',   m: 'Def-3DGS',         p: '–',  metric: 'PSNR ↑',  v: 30.42, code: true,  src: 'Tbl. 2, p. 6', conf: 0.91, s: 'verified' },
  { d: 'D-NeRF',      t: 'Dynamic NVS', c: '8 views, 200 frames',   m: '4DGS-Cache',       p: 'p4', metric: 'PSNR ↑',  v: 31.21, code: false, src: 'Tbl. 1, p. 5', conf: 0.88, s: 'auto' },
  { d: 'D-NeRF',      t: 'Dynamic NVS', c: '8 views, 200 frames',   m: 'GS-DiT',           p: 'p1', metric: 'LPIPS ↓', v: 0.041, code: true,  src: 'Tbl. 2, p. 6', conf: 0.94, s: 'verified' },
  { d: 'D-NeRF',      t: 'Dynamic NVS', c: '8 views, 200 frames',   m: 'GS-DiT',           p: 'p1', metric: 'FPS ↑',   v: 84,    code: true,  src: 'Fig. 5, p. 7', conf: 0.92, s: 'corrected' },
  { d: 'HyperNeRF',   t: 'Dynamic NVS', c: 'casual capture',        m: '4DGS-Cache',       p: 'p4', metric: 'PSNR ↑',  v: 31.20, code: false, src: 'Tbl. 1, p. 5', conf: 0.83, s: 'auto' },
  { d: 'HyperNeRF',   t: 'Dynamic NVS', c: 'casual capture',        m: 'Def-3DGS',         p: '–',  metric: 'PSNR ↑',  v: 28.91, code: true,  src: 'Tbl. 1, p. 4', conf: 0.95, s: 'verified' },
  { d: 'ALOHA',       t: 'Bimanual',    c: '50-trial real robot',   m: 'OpenVLA-2',        p: 'p2', metric: 'Succ ↑',  v: 0.714, code: true,  src: 'Tbl. 3, p. 8', conf: 0.90, s: 'verified', warn: 'fair-comparison' },
  { d: 'ALOHA',       t: 'Bimanual',    c: '50-trial real robot',   m: 'OpenVLA',          p: '–',  metric: 'Succ ↑',  v: 0.571, code: true,  src: 'Tbl. 3, p. 8', conf: 0.93, s: 'verified' },
  { d: 'ALOHA',       t: 'Bimanual',    c: '50-trial real robot',   m: 'RoboFM-13B',       p: 'p5', metric: 'Succ ↑',  v: 0.683, code: true,  src: 'Tbl. 5, p. 9', conf: 0.84, s: 'auto' },
  { d: 'Bridge-V3',   t: 'Manipulation',c: 'zero-shot, sim',        m: 'RoboFM-13B',       p: 'p5', metric: 'Succ ↑',  v: 0.621, code: true,  src: 'Tbl. 4, p. 8', conf: 0.89, s: 'verified' },
  { d: 'DL3DV-10K',   t: 'Sparse 3D',   c: '3 views',               m: 'SV-3DGS',          p: 'p3', metric: 'PSNR ↑',  v: 26.81, code: true,  src: 'Tbl. 2, p. 6', conf: 0.92, s: 'verified' },
  { d: 'MipNeRF-360', t: 'Static NVS',  c: 'compressed, 28 MB',     m: 'TriCompress',      p: 'p6', metric: 'PSNR ↑',  v: 25.94, code: true,  src: 'Tbl. 3, p. 7', conf: 0.91, s: 'verified' },
];

const PROJECT_BOARD = {
  name: 'Server-rack digital twin',
  description: 'Photorealistic 4D digital twin of a hyperscale rack for predictive maintenance and operator training. Target: 30 Hz interactive twin, < 50 cm pose error, robust to fluorescent flicker.',
  columns: [
    {
      id: 'collected', label: 'Collected', color: 'var(--fg-3)',
      cards: [
        { pid: 'p8', value: 'baseline pose recovery for capture rig', diff: 2 },
        { pid: 'p6', value: 'edge deployment on Jetson Orin', diff: 3 },
      ],
    },
    {
      id: 'reading', label: 'Reading', color: 'var(--info)',
      cards: [
        { pid: 'p3', value: 'sparse-view fits our 4-camera setup', diff: 3 },
        { pid: 'p7', value: 'use to re-rank our benchmark table', diff: 1, note: 4 },
      ],
    },
    {
      id: 'important', label: 'Important', color: 'var(--acc-violet)',
      cards: [
        { pid: 'p1', value: 'thin metal geometry, real-time fps', diff: 4, note: 6, bench: '31.74 PSNR' },
        { pid: 'p4', value: 'deformation cache → flicker mitigation', diff: 3, note: 2, bench: '120 Hz' },
      ],
    },
    {
      id: 'repro', label: 'Repro candidate', color: 'var(--acc-cyan)',
      cards: [
        { pid: 'p1', value: 'fork → run on our rack dataset', diff: 4, note: 3, code: true },
      ],
    },
    {
      id: 'implementing', label: 'Implementing', color: 'var(--acc-indigo)',
      cards: [
        { pid: 'p3', value: 'integrated 3-view distillation, week 2', diff: 3, note: 8, code: true },
      ],
    },
    {
      id: 'used', label: 'Used in presentation', color: 'var(--ok)',
      cards: [
        { pid: 'p6', value: 'rate-distortion figure for KAIST review', diff: 2, note: 1 },
      ],
    },
  ],
};

export { PAPERS, BENCHMARKS, PROJECT_BOARD };
